import type { PayerType, Resident } from '@/entities/resident/model/types';
import { create } from '@/shared/lib/zustand';
import type { SelectOption } from '@/shared/ui';
import { immer } from 'zustand/middleware/immer';

const mockedResidentsData: Resident[] = [
  {
    id: '1',
    name: 'John Doe',
    facility: 'Baker City - Ashley Manor',
    flagged: true,
    activeAlerts: 2,
    createdAlerts: 5,
    daysSinceReview: 5,
    rate: 100,
    revenue: 1000,
    payerType: 'Medicaid',
    levelOfCare: 2,
    daysOnCareLevel: 30,
    lengthOfStay: 100,
  },
  {
    id: '2',
    name: 'Jane Doe',
    facility: 'Baker City - Ashley Manor',
    flagged: false,
    activeAlerts: 0,
    createdAlerts: 0,
    daysSinceReview: 20,
    rate: 80,
    revenue: 800,
    payerType: 'Private',
    levelOfCare: 1,
    daysOnCareLevel: 50,
    lengthOfStay: 50,
  },
  {
    id: '3',
    name: 'Josh Doe',
    facility: 'Baker City - Ashley Manor',
    flagged: true,
    activeAlerts: 1,
    createdAlerts: 1,
    daysSinceReview: 2,
    rate: 120,
    revenue: 400,
    payerType: 'Medicaid',
    levelOfCare: 3,
    daysOnCareLevel: 15,
    lengthOfStay: 20,
  },
];

type FacilityResidentsFiltersState = {
  currentPage: number;
  search: string;
  itemsCount: {
    total: number;
    filtered: number;
  };
  activeAlerts: SelectOption[] | null;
  payerTypes: PayerType[] | null;
  levelsOfCare: number[] | null;
  flagged: boolean | null;

  allResidents: Resident[];
  filteredFacilityResidents: Resident[];

  setFilters: (
    filters: Partial<Omit<FacilityResidentsFiltersState, 'setFilters'>>
  ) => void;
  applyFilters: () => void;
};

export const useFacilityResidentsFiltersStore =
  create<FacilityResidentsFiltersState>()(
    immer((set) => ({
      currentPage: 1,
      search: '',
      itemsCount: {
        total: mockedResidentsData.length,
        filtered: mockedResidentsData.length,
      },
      activeAlerts: null,
      payerTypes: null,
      levelsOfCare: null,
      flagged: null,
      allResidents: mockedResidentsData,
      filteredFacilityResidents: mockedResidentsData,

      setFilters: (filters) =>
        set((state) => {
          Object.assign(state, filters);
        }),

      applyFilters: () =>
        set((state) => {
          const {
            allResidents,
            activeAlerts,
            search,
            payerTypes,
            levelsOfCare,
            flagged,
          } = state;

          const filtered = allResidents.filter((r) => {
            const matchesSearch =
              !search || r?.name.toLowerCase().includes(search.toLowerCase());

            const matchesPayType =
              !payerTypes?.length ||
              payerTypes.includes(r.payerType as PayerType);
            const matchesActiveAlerts =
              !activeAlerts?.length ||
              activeAlerts.some(({ value }) => {
                if (value === '>25') return r.activeAlerts > 25;
                if (value === '<50') return r.activeAlerts < 50;
                if (value === '26-50')
                  return r.activeAlerts >= 26 && r.activeAlerts <= 50;
                return false;
              });
            const matchesLevelOfCare =
              !levelsOfCare?.length || levelsOfCare.includes(r.levelOfCare);

            const matchesFlagged = flagged === null || flagged === r.flagged;

            return (
              matchesSearch &&
              matchesActiveAlerts &&
              matchesPayType &&
              matchesLevelOfCare &&
              matchesFlagged
            );
          });

          state.filteredFacilityResidents = filtered;
          state.itemsCount.filtered = filtered.length;
        }),
    }))
  );
